import React from "react";
import styles from "./ui.module.css";
import { tokens } from "../../design-system/tokens";

export type ButtonColor = "green" | "blue" | "red" | "gray";
export type ButtonSize = "small" | "medium" | "large";

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  color?: ButtonColor;
  size?: ButtonSize;
  width?: number | string;
  minWidth?: number | string;
  height?: number | string;
};

function toCssLength(value: number | string | undefined): string | undefined {
  if (value === undefined) return undefined;
  if (typeof value === "number") return `${value}px`;
  return value;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className = "",
      type = "button",
      color = "blue",
      size = "medium",
      width,
      minWidth,
      height,
      style,
      ...rest
    },
    ref
  ) => {
    const sizeClass =
      size === "small"
        ? styles.Small
        : size === "large"
          ? styles.Large
          : styles.Medium;
    const colorClass =
      color === "green"
        ? styles.Green
        : color === "red"
          ? styles.Red
          : color === "gray"
            ? styles.Gray
            : styles.Blue;

    const typography =
      size === "small"
        ? tokens.typography.buttonSmall
        : tokens.typography.button;

    const borderRadius =
      size === "small"
        ? tokens.borderRadius.md
        : size === "medium"
          ? tokens.borderRadius.lg
          : tokens.borderRadius.xl;

    const mergedStyle: React.CSSProperties = {
      fontFamily: typography.fontFamily,
      fontWeight: typography.fontWeight,
      borderRadius,
      ...(style || {}),
    };
    const w = toCssLength(width);
    const mw = toCssLength(minWidth);
    const h = toCssLength(height);
    if (w !== undefined) mergedStyle.width = w;
    if (mw !== undefined) mergedStyle.minWidth = mw;
    if (h !== undefined) mergedStyle.height = h;

    return (
      <button
        ref={ref}
        type={type}
        // Opt out of App.css `button:not(.ui-elements-button)` blue background
        // so color classes (Gray/Blue/…) can apply at rest, not only on :hover.
        className={`ui-elements-button ${styles.Button} ${sizeClass} ${colorClass}${
          className ? ` ${className}` : ""
        }`}
        style={mergedStyle}
        {...rest}
      >
        {children}
      </button>
    );
  }
);

Button.displayName = "Button";

export default Button;
